import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Chip,
    IconButton,
    Grid,
    Paper,
    Pagination,
    PaginationItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useDispatch, useSelector } from "react-redux";
import { getAllZonesAction, clearGetAllZonesAction } from "../../../store/actions";
import AddZone from "./AddZone";


const Zone = () => {
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedZoneId, setSelectedZoneId] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;
    const dispatch = useDispatch();

    const { successData: zonesData, loading: zonesLoading } = useSelector((state) => state.getAllZonesReducer || {});
    // Use zonesData.data.zones directly
    const zones = (zonesData && zonesData.data && Array.isArray(zonesData.data.zones)) ? zonesData.data.zones : [];

    // Search
    const filteredZones = useMemo(() => {
        const q = (searchQuery || "").toString().trim().toLowerCase();
        if (!q) return zones;
        return zones.filter((z) => {
            const zoneId = (z.zoneId ?? "").toString().toLowerCase();
            const zoneName = (z.zoneName ?? "").toLowerCase();
            return zoneId.includes(q) || zoneName.includes(q);
        });
    }, [zones, searchQuery]);

    // Pagination
    const totalPages = useMemo(() => {
        const pages = Math.ceil(filteredZones.length / rowsPerPage);
        return Math.max(1, pages || 1);
    }, [filteredZones.length]);

    const paginatedZones = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredZones.slice(start, start + rowsPerPage);
    }, [filteredZones, page]);

    const showingStart = filteredZones.length === 0 ? 0 : ((page - 1) * rowsPerPage + 1);
    const showingEnd = Math.min(page * rowsPerPage, filteredZones.length);


    // Prevent duplicate getAllZonesAction dispatches (React 18 StrictMode)
    const didFetchZones = React.useRef(false);
    useEffect(() => {
        if (!didFetchZones.current) {
            dispatch(clearGetAllZonesAction());
            dispatch(getAllZonesAction());
            didFetchZones.current = true;
        }
    }, [dispatch]);

    useEffect(() => {
        // Reset page when search filter changes
        setPage(1);
    }, [searchQuery]);

    useEffect(() => {
        // Clamp page if total pages shrink
        if (page > totalPages) {
            setPage(1);
        }
    }, [totalPages, page]);

    const handleAdd = () => {
        setIsEdit(false);
        setSelectedZoneId("");
        setOpen(true);
    };

    const handleEdit = (zone) => {
        const zoneId = zone?.zoneId;
        if (!zoneId && zoneId !== 0) return;
        setIsEdit(true);
        setSelectedZoneId(String(zoneId));
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setIsEdit(false);
        setSelectedZoneId("");
    };

    // const getStatusChipStyles = (status) => {
    // Removed getStatusChipStyles. Status color logic will be applied directly in Chip sx.

    return (
        <Box sx={{ minHeight: '100vh', width: '100%', mt: 2 }}>
            {/* Header */}
            <Typography fontSize={18} fontWeight={600} color="#111827" mb={2}>
                Zone Management
            </Typography>

            {/* Search + Button */}
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    mb: 3,
                    p: 2,
                    backgroundColor: '#fff',
                    borderRadius: 3,
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                }}
            >
                <TextField
                    placeholder="Search ..."
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                        width: { xs: 300, sm: 300 },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                        },
                        '& .MuiInputBase-input': {
                            borderRadius: 3,
                        },
                    }}
                />

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                        bgcolor: "#4F46E5",
                        textTransform: "none",
                        borderRadius: 3,
                        px: 3,
                        width: { xs: 300, sm: 300 },
                    }}
                    onClick={handleAdd}
                >
                    Add New Zone
                </Button>
            </Box>

            {/* Desktop (sm+) grid header and rows */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {/* Header */}
                <Box
                    display={{ xs: 'none', sm: 'grid' }}
                    gridTemplateColumns={{ sm: '1.2fr 2fr 1fr 1fr' }}
                    alignItems="center"
                    px={{ xs: 1, sm: 2 }}
                    py={{ xs: 1, sm: 2 }}
                    mb={{ xs: 1, sm: 1 }}
                >
                    {['ZONE ID', 'ZONE NAME', 'STATUS', 'ACTIONS'].map((label, i) => (
                        <Typography
                            key={label}
                            fontSize={{ sm: 10, md: 12 }}
                            color="#000000"
                            fontWeight={700}
                            sx={{
                                textTransform: 'uppercase',
                                textAlign: i === 2 ? 'center' : i === 3 ? 'right' : 'left',
                            }}
                        >
                            {label}
                        </Typography>
                    ))}
                </Box>

                {/* Rows */}
                {zonesLoading && (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', px: 2, py: 1.5, mb: 2 }}>
                        <Typography variant="body2">Loading zones...</Typography>
                    </Paper>
                )}
                {!zonesLoading && paginatedZones.map((z) => {
                    const status = (z.status || '').toLowerCase();
                    const chipSx = {
                        width: 90,
                        height: 24,
                        backgroundColor: status === 'active' ? '#D1FAE5' : status === 'inactive' ? '#FECACA' : '#D1FAE5',
                        color: status === 'active' ? '#047857' : status === 'inactive' ? '#B91C1C' : '#047857',
                        '& .MuiChip-label': { fontSize: 12, fontWeight: 600 },
                    };
                    return (
                        <Paper
                            key={z.zoneId}
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                display: { xs: 'none', sm: 'grid' },
                                gridTemplateColumns: { sm: '1.2fr 2fr 1fr 1fr' },
                                alignItems: 'center',
                                border: '1px solid #E5E7EB',
                                px: 2,
                                py: 1.5,
                                gap: 0,
                                columnGap: 0,
                                mb: 1.25,
                                '&:hover': {
                                    border: '2px solid #2563EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                },
                            }}
                        >
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{z.zoneId}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{z.zoneName}</Typography>
                            <Box sx={{ justifySelf: 'center' }}>
                                <Chip
                                    label={String(z.status ? z.status : 'Active').charAt(0).toUpperCase() + String(z.status ? z.status : 'Active').slice(1)}
                                    size="small"
                                    sx={chipSx}
                                />
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end" sx={{ justifySelf: 'end' }}>
                                <IconButton size="small" aria-label="edit zone" onClick={() => handleEdit(z)}>
                                    <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                </IconButton>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>

            {/* Cards for xs screens */}
            <Grid container spacing={2} sx={{ display: { xs: 'flex', sm: 'none' } }} justifyContent="center">
                {!zonesLoading && paginatedZones.map((zone) => {
                    return (
                        <Grid item xs={12} key={zone.zoneId} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    width: 300,
                                    backgroundColor: '#fff',
                                    border: '1px solid #E5E7EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                    '&:hover': {
                                        border: '2px solid #2563EB',
                                        boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                                    <div />
                                    {/* Status color logic directly in Chip sx */}
                                    <Chip
                                        label={zone.status ? zone.status : 'Active'}
                                        size="small"
                                        sx={{
                                            backgroundColor: (zone.status || '').toLowerCase() === 'active' ? '#D1FAE5' : (zone.status || '').toLowerCase() === 'inactive' ? '#FECACA' : '#D1FAE5',
                                            color: (zone.status || '').toLowerCase() === 'active' ? '#047857' : (zone.status || '').toLowerCase() === 'inactive' ? '#B91C1C' : '#047857',
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" fontWeight={600} color="#0F172A" sx={{ mb: 0.5 }}>
                                        Zone: <span style={{ fontWeight: 600 }}>{zone.zoneName}</span>
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} color="#0F172A">
                                        Zone ID: <span style={{ fontWeight: 600 }}>{zone.zoneId}</span>
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <IconButton size="small" onClick={() => handleEdit(zone)}><EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB' }} /></IconButton>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Footer Pagination */}
            {!zonesLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        Showing {showingStart}-{showingEnd} of {filteredZones.length}
                    </Typography>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        size="small"
                        renderItem={(item) => (
                            <PaginationItem
                                {...item}
                                sx={{
                                    borderRadius: '50%',
                                    minWidth: 32,
                                    height: 32,
                                    '&.Mui-selected': {
                                        backgroundColor: '#2563EB',
                                        color: '#FFFFFF',
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: '#2563EB',
                                        color: '#FFFFFF',
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
            )}

            {/* Add/Edit Zone Dialog */}
            <AddZone open={open} onClose={handleCloseDialog} isEdit={isEdit} zoneId={selectedZoneId} />
        </Box>
    );
};

export default Zone;
